/* eslint-disable no-use-before-define */

import { useEffect, useState, useRef, useContext } from "react";
import { IOSDeviceOrientationEvent } from "../../customHooks/useGyroSensor";
import LevelScreen from "./LevelScreen";
import ScanningComponent from "./ScanningComponent";
import { AngleDetectorProps } from "../../types/interfaces";
import ParamsContext from "../../utils/context/paramsContext";

export default function AngleDetector({
  config,
}: AngleDetectorProps) {
  const {
    scanUniqueKey,
    userDetails,
    setIsVideoUploaded,
    setScanFailsError,
    setScanStartTime,
    setScanUniqueKey,
  } = useContext(ParamsContext);
  const [angle, setAngle] = useState(90);
  const [calibrationOffset, setCalibrationOffset] = useState(0);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [stabilityScore, setStabilityScore] = useState(0);
  const lastAnglesRef = useRef<number[]>([]);
  const [isScanLocked, setIsScanLocked] = useState(false);
  const calibratedAngle = angle - calibrationOffset;
  const isInTargetRange = calibratedAngle >= 80 && calibratedAngle <= 95;

  const resetDetector = () => {
    setAngle(90);
    setCalibrationOffset(0);
    setCountdown(null);
    setIsScanning(false);
    setStabilityScore(0);
    setIsScanLocked(false);
    lastAnglesRef.current = [];
  };

  useEffect(() => {
    if (typeof window !== "undefined" && window.DeviceOrientationEvent) {
      const handleOrientation = (event: DeviceOrientationEvent) => {
        if (event.beta !== null) {
          let newAngle = Math.abs(event.beta);
          if (event.gamma !== null) {
            newAngle *= Math.cos((event.gamma * Math.PI) / 180);
          }
          newAngle = Math.max(0, Math.min(180, newAngle));
          setAngle(newAngle);
        }
      };

      const requestPermission = async () => {
        const DeviceOrientation =
          DeviceOrientationEvent as unknown as IOSDeviceOrientationEvent;

        if (
          DeviceOrientation &&
          typeof DeviceOrientation.requestPermission === "function"
        ) {
          try {
            const permission = await DeviceOrientation.requestPermission();
            if (permission === "granted") {
              // wrappedHandler = timeout(handleOrientation, 2000);
              window.addEventListener("deviceorientation", handleOrientation);
            }
          } catch (e) {
            console.error("Permission error", e);
          }
        } else {
          // wrappedHandler = handleOrientation;
          window.addEventListener("deviceorientation", handleOrientation);
        }

        return () => {
          if (handleOrientation) {
            window.removeEventListener("deviceorientation", handleOrientation);
          }
        };
      };

      document.addEventListener("click", requestPermission, { once: true });
    }
  }, []);

  useEffect(() => {
    lastAnglesRef.current = [
      ...lastAnglesRef.current.slice(-4),
      calibratedAngle,
    ];

    if (lastAnglesRef.current.length >= 5) {
      const variation =
        Math.max(...lastAnglesRef.current) - Math.min(...lastAnglesRef.current);
      if (isInTargetRange && variation < 2) {
        setStabilityScore((prev) => Math.min(100, prev + 10));
      } else {
        setStabilityScore((prev) => Math.max(0, prev - 20));
      }
    }

    if (!isInTargetRange && (countdown !== null || isScanning)) {
      resetCountdown();
    }
  }, [calibratedAngle, isInTargetRange, countdown, isScanning]);

	useEffect(() => {
		if (stabilityScore >= 100 && countdown === null && !isScanning ) {
			startCountdown();
		}
		if (stabilityScore < 50 && countdown !== null) {
			resetCountdown();
		}
	}, [stabilityScore, countdown, isScanning]);

  const startCountdown = () => {
    setCountdown(3);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(timer);
          setIsScanning(true);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const resetCountdown = () => {
    setCountdown(null);
    if (!isScanLocked) {
      setIsScanning(false);
    }
  };


  return (
    <LevelScreen
      angle={calibratedAngle}
      countdown={countdown}
      isScanning={isScanning}
      isInTargetRange={isInTargetRange}
      stabilityScore={stabilityScore}
    >
      {isScanning && (
        <ScanningComponent
          setIsScanLocked={setIsScanLocked}
          resetDetector={resetDetector}
          scanID={scanUniqueKey}
          userDetails={userDetails}
          setIsVideoUploaded={setIsVideoUploaded}
          setScanFailsError={setScanFailsError}
          setScanStartTime={setScanStartTime}
          setScanUniqueKey={setScanUniqueKey}
        />
      )}
    </LevelScreen>
  );
}
